import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { processAffiliateParams } from './affiliateHelpers';

/**
 * Global referral tracking hook
 * Automatically processes URL parameters and tracks referral visits
 */
export const useReferralTracking = () => {
  const location = useLocation();
  const hasTrackedVisit = useRef(false);
  const lastProcessedSearch = useRef('');

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const currentSearch = location.search;

    // Only process if we haven't already tracked this visit or if URL params changed
    if (!hasTrackedVisit.current || currentSearch !== lastProcessedSearch.current) {
      console.log('🔍 Processing referral parameters for page:', location.pathname);
      
      // Process affiliate parameters from URL
      const affiliateData = processAffiliateParams(searchParams);
      
      if (affiliateData) {
        // Track the visit on the backend
        trackVisitOnBackend(affiliateData, searchParams);
        hasTrackedVisit.current = true;
        lastProcessedSearch.current = currentSearch;
      }
    }
  }, [location.search, location.pathname]);

  const trackVisitOnBackend = async (affiliateData, searchParams) => {
    try {
      // Get additional URL parameters
      const urlParams = Object.fromEntries(searchParams.entries());
      
      // Generate a session ID if not exists
      let sessionId = sessionStorage.getItem('referral_session_id');
      if (!sessionId) {
        sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem('referral_session_id', sessionId);
      }

      const visitData = {
        refCode: affiliateData.refCode,
        ipAddress: await getClientIP(),
        userAgent: navigator.userAgent,
        referrerUrl: document.referrer || null,
        pageUrl: window.location.href,
        sessionId,
        // URL Parameters
        firstName: urlParams.firstName || urlParams.first_name || null,
        lastName: urlParams.lastName || urlParams.last_name || null,
        email: urlParams.email || null,
        phone: urlParams.phone || null,
        fullName: urlParams.fullName || urlParams.full_name || urlParams.name || null,
        // UTM Parameters
        utmSource: urlParams.utm_source || urlParams.source || null,
        utmCampaign: urlParams.utm_campaign || urlParams.campaign || null,
        utmMedium: urlParams.utm_medium || urlParams.medium || null,
        utmTerm: urlParams.utm_term || null,
        utmContent: urlParams.utm_content || null
      };

      console.log('📊 Tracking referral visit:', {
        refCode: visitData.refCode,
        page: visitData.pageUrl,
        hasUserData: !!(visitData.firstName || visitData.lastName || visitData.email),
        utmSource: visitData.utmSource
      });

      // Send to backend
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/dataServer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'trackReferralVisit',
          payload: visitData
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Referral visit tracked successfully:', result);
      } else {
        console.warn('⚠️ Failed to track referral visit:', response.status);
      }
    } catch (error) {
      console.error('❌ Error tracking referral visit:', error);
      // Don't throw - tracking failures shouldn't break the app
    }
  };

  // Get client IP (best effort - may not work in all environments)
  const getClientIP = async () => {
    try {
      // Try to get IP from a simple API (fallback)
      const response = await fetch('https://api.ipify.org?format=json', { timeout: 3000 });
      const data = await response.json();
      return data.ip;
    } catch (error) {
      // Return null if we can't get IP - the backend will use the request IP
      return null;
    }
  };

  return {
    // Utility functions that components can use
    getCurrentAffiliateData: () => {
      const { getAffiliateData } = require('./affiliateHelpers');
      return getAffiliateData();
    },
    
    debugTracking: () => {
      const { debugAffiliateTracking } = require('./affiliateHelpers');
      return debugAffiliateTracking();
    }
  };
};

export default useReferralTracking;